import type { ActionTone } from "../types";

export const countText = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural.replace("{{count}}", String(count));

const getDateLocale = (): string => {
  const stored = typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : "";
  if (stored?.startsWith("nl")) return "nl-NL";
  if (stored?.startsWith("en")) return "en-US";
  return typeof navigator !== "undefined" && navigator.language ? navigator.language : "en-US";
};

export const formatDate = (value: any): string | null => {
  if (value === null || value === undefined || value === "") return null;

  const numericValue = Number(value);
  const date =
    Number.isFinite(numericValue) && String(value).trim() !== ""
      ? new Date(numericValue > 1e12 ? numericValue : numericValue * 1000)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString(getDateLocale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const formatDateTime = (value: any): string | null => {
  if (value === null || value === undefined || value === "") return null;

  const numericValue = Number(value);
  const date =
    Number.isFinite(numericValue) && String(value).trim() !== ""
      ? new Date(numericValue > 1e12 ? numericValue : numericValue * 1000)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return `${date.toLocaleDateString(getDateLocale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
  })} • ${date.toLocaleTimeString(getDateLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export const formatTimeRange = (start: any, end: any): string | null => {
  const startLabel = formatDateTime(start);
  const endLabel = formatDateTime(end);

  if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
  return startLabel || endLabel;
};

export const formatRange = (start: any, end: any): string | null => {
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);

  if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
  return startLabel || endLabel;
};

export const getDisplayName = (value: any): string => {
  if (!value) return "";

  if (typeof value === "string") return value;

  return (
    [
      value.firstName ?? value.firstname ?? value.givenName,
      value.lastName ?? value.lastname ?? value.familyName,
    ]
      .filter(Boolean)
      .join(" ") ||
    value.name ||
    value.fullName ||
    value.childName ||
    value.title ||
    ""
  );
};

export const getClassLabel = (value: any): string => {
  return (
    value?.classroomName ||
    value?.className ||
    value?.currentClassName ||
    value?.currentClassroom?.className ||
    value?.classroom?.className ||
    ""
  );
};

export const getChildAge = (value: any): number | null => {
  const raw =
    value?.dateBirth ||
    value?.dateOfBirth ||
    value?.birthDate ||
    value?.birthday ||
    null;

  if (!raw) return null;

  const birthDate = new Date(raw);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

export const getRequestTitle = (request: any): string => {
  const directName =
    request?.childName ||
    request?.targetName ||
    request?.displayName ||
    request?.name ||
    request?.title;

  if (directName) return directName;

  if (request?.child) {
    const childName = getDisplayName(request.child);
    if (childName) return childName;
  }

  return "Verzoek";
};

export const getRequestSubtitle = (request: any): string => {
  const type = String(
    request?.entityType ||
      request?.targetType ||
      request?.type ||
      request?.requestType ||
      "",
  ).toUpperCase();

  const action = String(
    request?.action || request?.mutationType || "",
  ).toUpperCase();

  if (type === "CHILD") return "Wijzigingsverzoek kind";
  if (type === "PARENT") return "Wijzigingsverzoek ouder";
  if (type === "FAMILY") return "Wijzigingsverzoek gezin";
  if (type === "BANK_ACCOUNT") return "Wijziging bankrekening";
  if (action === "CREATE") return "Nieuw verzoek";
  if (action === "UPDATE") return "Wijzigingsverzoek";
  if (action === "DELETE") return "Verwijderverzoek";

  return request?.description || request?.summary || "Wijzigingsverzoek";
};

export const getToneClasses = (tone: ActionTone) => {
  if (tone === "urgent") {
    return {
      side: "bg-red-50",
      iconWrap: "text-rose-500",
      eyebrow: "text-red-600",
      button: "bg-red-50 text-red-700",
      dot: "bg-red-500",
      statIcon: "text-red-500",
      statValue: "text-red-600",
    };
  }

  if (tone === "waiting") {
    return {
      side: "bg-amber-50",
      iconWrap: "text-amber-500",
      eyebrow: "text-amber-700",
      button: "bg-amber-50 text-amber-700",
      dot: "bg-amber-500",
      statIcon: "text-amber-500",
      statValue: "text-amber-600",
    };
  }

  return {
    side: "bg-emerald-50",
    iconWrap: "text-emerald-700",
    eyebrow: "text-emerald-700",
    button: "bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
    statIcon: "text-emerald-700",
    statValue: "text-emerald-700",
  };
};
